import { useEffect, useRef, useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, Skeleton } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { useBillingCopy } from '@/lib/public-config';
import { useAuth } from '@/lib/auth';
import { api, ApiError, formatDate, type PlansResponse } from '@/lib/utils';

declare global {
  interface Window {
    Paddle?: {
      Initialize: (opts: {
        token: string;
        environment?: 'sandbox' | 'production';
        eventCallback?: (data: { name?: string; data?: unknown }) => void;
      }) => void;
      Environment?: {
        set: (environment: 'sandbox' | 'production') => void;
      };
      Checkout: {
        open: (opts: {
          transactionId?: string;
          items?: Array<{ priceId: string; quantity: number }>;
          customer?: { email: string };
          customData?: Record<string, string>;
          settings?: { successUrl?: string };
        }) => void;
      };
    };
  }
}

function initPaddle(opts: {
  token: string;
  environment: 'sandbox' | 'production';
  eventCallback?: (data: { name?: string; data?: unknown }) => void;
}) {
  const paddle = window.Paddle;
  if (!paddle) throw new Error('Paddle checkout unavailable');
  paddle.Initialize({
    token: opts.token,
    eventCallback: opts.eventCallback,
  });
  paddle.Environment?.set(opts.environment);
}

function loadPaddleScript(): Promise<void> {
  if (window.Paddle) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-paddle]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    s.async = true;
    s.dataset.paddle = 'true';
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Paddle.js'));
    document.head.appendChild(s);
  });
}

interface CheckoutPaddleResponse {
  provider: 'paddle';
  transactionId?: string;
  upgraded?: boolean;
  planTier?: string | null;
  clientToken: string;
  paddleEnvironment: 'sandbox' | 'production';
  successUrl: string;
}

interface CheckoutDodoResponse {
  provider: 'dodo';
  url?: string;
  upgraded?: boolean;
  planTier?: string | null;
  dodoEnvironment?: 'test' | 'live';
  successUrl: string;
}

interface CheckoutStripeResponse {
  provider: 'stripe';
  url: string;
}

function paddleCheckoutCompleted(evt: { name?: string; data?: unknown }) {
  if (evt.name !== 'checkout.completed') return;
  const data = evt.data as Record<string, unknown> | undefined;
  const transactionId =
    (typeof data?.transaction_id === 'string' && data.transaction_id) ||
    (typeof data?.id === 'string' && data.id) ||
    undefined;
  void (async () => {
    try {
      await api('/api/v1/settings/billing/sync', {
        method: 'POST',
        body: JSON.stringify({ transaction_id: transactionId }),
      });
    } catch {
      /* webhook will catch up */
    }
    window.location.href = '/settings/billing?success=1';
  })();
}

export function BillingPage() {
  const search = useSearch({ strict: false }) as { success?: string; canceled?: string };
  const paddleReady = useRef(false);
  const billing = useBillingCopy();
  const { usage, refresh } = useAuth();
  const queryClient = useQueryClient();
  const [planSyncing, setPlanSyncing] = useState(search.success === '1');

  const onTrial = Boolean(usage?.onTrial);
  const subscribedTier = usage?.subscribedTier ?? null;
  const hasActiveSubscription = Boolean(usage?.hasActiveSubscription);
  const effectiveTier = usage?.tier ?? null;

  const { data, isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api<PlansResponse>('/api/v1/plans'),
  });

  const provider = data?.billingProvider ?? billing.provider;

  useEffect(() => {
    if (search.success !== '1') return;
    let cancelled = false;
    const syncPlan = async () => {
      setPlanSyncing(true);
      const params = new URLSearchParams(window.location.search);
      const subscriptionId = params.get('subscription_id');
      for (let i = 0; i < 12 && !cancelled; i++) {
        try {
          await api('/api/v1/settings/billing/sync', {
            method: 'POST',
            body: JSON.stringify({
              subscription_id: subscriptionId ?? undefined,
            }),
          });
        } catch {
          /* provider may still be processing — retry */
        }
        const me = await refresh();
        await queryClient.invalidateQueries({ queryKey: ['plans'] });
        if (me?.usage?.hasActiveSubscription) {
          if (!cancelled) setPlanSyncing(false);
          return;
        }
        if (i < 11) await new Promise((r) => setTimeout(r, 1500));
      }
      if (!cancelled) setPlanSyncing(false);
    };
    void syncPlan();
    return () => {
      cancelled = true;
    };
  }, [search.success, refresh, queryClient]);

  useEffect(() => {
    if (provider !== 'paddle' || !data?.paddleClientToken || paddleReady.current) return;
    loadPaddleScript()
      .then(() => {
        initPaddle({
          token: data.paddleClientToken!,
          environment: data.paddleEnvironment ?? 'sandbox',
          eventCallback: paddleCheckoutCompleted,
        });
        paddleReady.current = true;
      })
      .catch(() => {
        /* Paddle.js failed — checkout button will show error */
      });
  }, [provider, data?.paddleClientToken, data?.paddleEnvironment]);

  const checkout = async (planId: string) => {
    try {
      const res = await api<CheckoutPaddleResponse | CheckoutDodoResponse | CheckoutStripeResponse>(
        '/api/v1/settings/billing/checkout',
        { method: 'POST', body: JSON.stringify({ price_id: planId }) },
      );
      if (res.provider === 'stripe' && res.url) {
        window.location.href = res.url;
        return;
      }
      if (res.provider === 'dodo') {
        if (res.upgraded) {
          await refresh();
          window.location.href = '/settings/billing?success=1';
          return;
        }
        if (res.url) {
          window.location.href = res.url;
        }
        return;
      }
      if (res.provider === 'paddle') {
        if (res.upgraded) {
          await refresh();
          window.location.href = '/settings/billing?success=1';
          return;
        }
        await loadPaddleScript();
        const environment = res.paddleEnvironment ?? 'sandbox';
        if (!paddleReady.current) {
          initPaddle({
            token: res.clientToken,
            environment,
            eventCallback: paddleCheckoutCompleted,
          });
          paddleReady.current = true;
        } else {
          window.Paddle?.Environment?.set(environment);
        }
        window.Paddle!.Checkout.open({
          transactionId: res.transactionId!,
          settings: { successUrl: res.successUrl },
        });
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'DODO_PLAN_CHANGE_PENDING') {
        const openPortal = err.fields?.portalUrl
          ? window.confirm(`${err.message}\n\nOpen billing portal now?`)
          : false;
        if (openPortal && err.fields?.portalUrl) {
          window.location.href = err.fields.portalUrl;
        } else {
          alert(err.message);
        }
        return;
      }
      throw err;
    }
  };

  const portal = async () => {
    try {
      const res = await api<{ url: string }>('/api/v1/settings/billing/portal', {
        method: 'POST',
        body: '{}',
      });
      if (res.url) window.location.href = res.url;
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'Could not open billing portal');
    }
  };

  const plans = data?.plans ?? [];
  const trialEnded = !onTrial && usage?.trialDaysLeft === 0 && usage?.trialEndsAt;
  const showExpiredTrialBanner = Boolean(trialEnded && !hasActiveSubscription);
  const morLabel = billing.morShortName;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader title="Billing &amp; plan" description="Choose the plan that fits your shop. Cancel anytime." />

      {search.success === '1' && planSyncing && (
        <div className="rounded-lg border border-brand/25 bg-brand/5 px-4 py-3 text-sm text-text-primary">
          Activating your plan… This usually takes a few seconds while {morLabel} confirms payment.
        </div>
      )}
      {search.success === '1' && !planSyncing && hasActiveSubscription && (
        <div className="rounded-lg bg-success-subtle px-4 py-3 text-sm text-[#166534]">
          Payment received — you&apos;re now on{' '}
          <strong>{usage?.subscribedTierName ?? usage?.tierName ?? 'your new plan'}</strong>.
        </div>
      )}
      {search.success === '1' && !planSyncing && !hasActiveSubscription && (
        <div className="rounded-lg bg-bg-muted px-4 py-3 text-sm text-text-secondary">
          Payment received. Your plan is still syncing — refresh this page in a moment if it doesn&apos;t update.
        </div>
      )}
      {search.canceled === '1' && (
        <div className="rounded-lg bg-bg-muted px-4 py-3 text-sm text-text-secondary">Checkout canceled — no charge made.</div>
      )}

      {usage?.subscriptionStatus === 'past_due' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          Your last payment didn&apos;t go through. Update your payment method in the billing portal to keep your plan
          active — {morLabel} will retry automatically.
        </div>
      )}

      {usage?.subscriptionCancelAt && hasActiveSubscription && (
        <div className="rounded-lg border border-border bg-bg-muted px-4 py-3 text-sm text-text-secondary">
          Your subscription is scheduled to cancel on{' '}
          <strong className="text-text-primary">{formatDate(usage.subscriptionCancelAt.slice(0, 10))}</strong>. You&apos;ll
          keep full access until then.
        </div>
      )}

      {showExpiredTrialBanner && (
        <div className="rounded-lg border border-brand/25 bg-brand/5 px-4 py-3 text-sm text-text-primary">
          Your free trial has ended. Choose a plan below to keep renewal reminders, campaigns, and premium features.
        </div>
      )}

      {billing.isMerchantOfRecord && (
        <p className="text-[13px] text-text-secondary">
          Subscriptions are processed securely by <strong>{billing.morShortName}</strong> (Merchant of Record). Taxes
          and invoices are handled by {billing.morShortName}.
        </p>
      )}

      {(onTrial || hasActiveSubscription || effectiveTier) && (
        <Card title="Your current plan">
          <p className="text-sm text-text-primary">
            {planSyncing ? (
              <>Checking your subscription with {morLabel}…</>
            ) : onTrial && !hasActiveSubscription ? (
              <>
                You&apos;re trying <strong>{usage?.tierName ?? 'Pro'}</strong> features free
                {usage?.trialDaysLeft != null && usage.trialDaysLeft > 0
                  ? ` — ${usage.trialDaysLeft} ${usage.trialDaysLeft === 1 ? 'day' : 'days'} left`
                  : ''}
                . Subscribe below to keep access when your trial ends.
              </>
            ) : hasActiveSubscription && subscribedTier ? (
              <>
                You&apos;re subscribed to <strong>{usage?.subscribedTierName ?? subscribedTier}</strong>
                {usage?.subscriptionStatus === 'past_due' ? ' (payment issue — see above)' : ''}.
              </>
            ) : (
              <>
                You&apos;re on <strong>{usage?.tierName ?? 'Lite'}</strong> (free tier).
              </>
            )}
          </p>
        </Card>
      )}

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-border bg-bg-surface p-6 shadow-card">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="mt-4 h-9 w-24" />
              </div>
            ))
          : plans.map((plan) => {
              const isPaidCurrent = hasActiveSubscription && subscribedTier === plan.id;
              const isTrialIncluded = onTrial && !hasActiveSubscription && plan.id === effectiveTier;

              return (
                <div
                  key={plan.id}
                  className={`relative flex flex-col rounded-2xl border bg-bg-surface p-6 shadow-card ${
                    isPaidCurrent
                      ? 'border-success ring-1 ring-success/40'
                      : plan.recommended && !isPaidCurrent
                        ? 'border-brand ring-1 ring-brand/30'
                        : 'border-border'
                  }`}
                >
                  {isPaidCurrent && (
                    <span className="absolute -top-2.5 left-5 rounded-full bg-success px-2.5 py-0.5 text-[11px] font-semibold text-white">
                      Current plan
                    </span>
                  )}
                  {isTrialIncluded && (
                    <span className="absolute -top-2.5 left-5 rounded-full bg-brand/90 px-2.5 py-0.5 text-[11px] font-semibold text-white">
                      Included in trial
                    </span>
                  )}
                  {plan.recommended && !isPaidCurrent && (
                    <span className="absolute -top-2.5 right-5 rounded-full bg-brand px-2.5 py-0.5 text-[11px] font-semibold text-white">
                      Most popular
                    </span>
                  )}
                  <h3 className="text-base font-semibold">{plan.name}</h3>
                  <p className="mt-1 min-h-[2.5rem] text-[13px] text-text-secondary">{plan.tagline}</p>
                  <p className="mt-3">
                    <span className="text-[32px] font-semibold tracking-tight">${plan.priceMonthly}</span>
                    <span className="text-text-secondary">/mo</span>
                  </p>
                  <ul className="mt-5 flex-1 space-y-2.5">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-[13px]">
                        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-success-subtle">
                          <Check className="h-2.5 w-2.5 text-success" />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={isPaidCurrent ? 'secondary' : plan.recommended ? 'primary' : 'secondary'}
                    className="mt-6 w-full"
                    disabled={!plan.priceId || isPaidCurrent}
                    onClick={() => checkout(plan.id)}
                  >
                    {!plan.priceId
                      ? 'Coming soon'
                      : isPaidCurrent
                        ? 'Current plan'
                        : isPaidCurrent === false && hasActiveSubscription && subscribedTier
                          ? `Switch to ${plan.name}`
                          : `Choose ${plan.name}`}
                  </Button>
                </div>
              );
            })}
      </div>

      <Card
        title="Manage subscription"
        description={
          hasActiveSubscription
            ? `Update payment method, view invoices, change plan, or cancel via ${billing.morShortName}.`
            : 'After you subscribe, manage billing and invoices here.'
        }
      >
        <Button variant="secondary" onClick={portal} disabled={!hasActiveSubscription}>
          Open billing portal <ExternalLink className="h-4 w-4" />
        </Button>
        {!hasActiveSubscription && (
          <p className="mt-2 text-[13px] text-text-muted">Subscribe to a plan first to access the billing portal.</p>
        )}
      </Card>
    </div>
  );
}
